/**
 * Last-Write-Wins (LWW) conflict resolver.
 *
 * Pure functions — no storage, no network, no clock. Given two candidate
 * records (local + remote), return the winner using timestamp comparison.
 *
 * Semantics match the legacy backend's conflict policy (server-side LWW in
 * `backend/src/household/syncService.ts::processClientEvent`):
 *
 *   - Whichever side has the later `updated_at` (server calls it `timestamp`)
 *     wins.
 *   - Ties go to REMOTE (server authority). Legacy backend uses `>`
 *     ("server timestamp > client timestamp" triggers `server_wins`); the
 *     inverse of that condition — `server <= client` — means the client's
 *     write is the later one and applies. But when the two are EQUAL, the
 *     server's existing record was recorded first and the server is the
 *     tiebreaker of truth. Follow that rule here.
 *   - Missing timestamps are treated as `-Infinity` (never the winner
 *     against a real timestamp). Two missing timestamps → remote wins.
 *
 * Timestamps are ISO-8601 strings. Lexicographic comparison is valid for
 * ISO-8601 in UTC form (which is what legacy always produces).
 */

/**
 * Minimal shape the resolver cares about — both inputs must expose an
 * `updated_at` that's either an ISO string or undefined.
 *
 * Using a structural type keeps the resolver reusable for HouseholdProduct,
 * ShoppingListItem, ShoppingList — any record with `updated_at`.
 */
export interface TimestampedRecord {
  readonly updated_at?: string;
}

/**
 * Result of an LWW decision. Callers pattern-match on `winner` to pick
 * which side to persist.
 */
export type LwwDecision = 'local' | 'remote';

/**
 * Compare two timestamps (ISO-8601 UTC). Returns:
 *   - negative if `a < b`
 *   - positive if `a > b`
 *   - 0 if equal (including both missing)
 *
 * Missing timestamps are treated as the smallest possible value.
 */
function compareTimestamps(
  a: string | undefined,
  b: string | undefined,
): number {
  if (a === undefined && b === undefined) return 0;
  if (a === undefined) return -1;
  if (b === undefined) return 1;
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
}

/**
 * Decide the winner between a local and remote record. See file header for
 * the full rule set. This function is the single source of truth for LWW
 * on the client; no other code should re-implement it.
 */
export function resolveLww<T extends TimestampedRecord>(
  local: T,
  remote: T,
): LwwDecision {
  const cmp = compareTimestamps(local.updated_at, remote.updated_at);
  if (cmp > 0) return 'local';
  // cmp <= 0 — remote is later OR equal. Either way, remote wins.
  return 'remote';
}

/**
 * Convenience: resolve + return the winning record. Same decision rules as
 * `resolveLww`. Useful at call sites that only want the record, not the
 * label.
 */
export function pickWinner<T extends TimestampedRecord>(
  local: T,
  remote: T,
): T {
  return resolveLww(local, remote) === 'local' ? local : remote;
}

/**
 * Three-way "incoming event vs existing record" decision — the shape the
 * sync engine hits during pull.
 *
 * `existing` may be undefined (no local record yet → always apply remote).
 * `remoteTimestamp` is the server-stamped timestamp on the incoming event.
 *
 * Returns `true` when the engine should apply the remote event, `false`
 * when the local record wins (remote is stale — skip).
 */
export function shouldApplyRemote(
  existing: TimestampedRecord | undefined,
  remoteTimestamp: string,
): boolean {
  if (existing === undefined) return true;
  // Synthesize a remote record shape to reuse `resolveLww`.
  return resolveLww(existing, { updated_at: remoteTimestamp }) === 'remote';
}
